// Northcrest Athletics — translations (EN / DE / PT)
// Keep keys flat & deeply scoped for easy editorial review.

export const SUPPORTED_LANGS = ["en", "de", "pt"];
export const DEFAULT_LANG = "en";

export const translations = {
  en: {
    nav: {
      home: "Home",
      about: "About Us",
      camps: "Camps",
      coachEd: "Coach Education",
      coachEdShort: "Coach Ed",
      partners: "Partners",
      legal: "Legal",
      contact: "Contact",
      universities: "Universities",
      sports: "Sports",
      successStories: "Success Stories",
      resources: "Resources",
      bookCta: "Book Consultation",
    },
    common: {
      learnMore: "Learn More",
      readMore: "Read More",
      explore: "Explore",
      back: "Back",
      contactUs: "Contact Us",
      sendMessage: "Send Message",
      submitting: "Sending…",
      ourOffices: "Our Offices",
      tagline: "North is the direction. Crest is the summit.",
      taglineSecondary: "From Europe to Opportunity.",
      seeAll: "See all",
    },
    home: {
      heroOverline: "Elite Athlete Advisory · Delaware · Bern · Lisbon · San Diego",
      heroTitle1: "Your Potential.",
      heroTitle2: "Your Future.",
      heroSubtitle:
        "Guiding ambitious student-athletes toward academic and athletic opportunities in the United States.",
      ctaPrimary: "Book a Consultation",
      ctaSecondary: "Learn More",
      whyTitle: "Why Northcrest",
      whyKicker: "Three pillars",
      pillars: [
        {
          k: "Academic Excellence",
          d: "Rigorous academic preparation and university placement aligned with each athlete’s ambition.",
        },
        {
          k: "Athletic Achievement",
          d: "Performance-driven development, recruitment strategy, and elite coach outreach.",
        },
        {
          k: "International Opportunity",
          d: "A trusted bridge from Europe to leading American universities and athletic programs.",
        },
      ],
      processTitle: "Our Process",
      processKicker: "A measured path. Six deliberate steps.",
      processSteps: [
        { n: "01", t: "Assessment", d: "A complete academic and athletic profile review." },
        { n: "02", t: "Strategy", d: "A bespoke advisory plan, calibrated to your ambition." },
        { n: "03", t: "University Matching", d: "Curated programs across NCAA, NAIA and NJCAA." },
        { n: "04", t: "Coach Outreach", d: "Direct, professional introductions to U.S. coaches." },
        { n: "05", t: "Placement", d: "Application support, scholarship guidance and offers." },
        { n: "06", t: "Success", d: "Long-term mentorship through arrival and beyond." },
      ],
      sportsTitle: "Sports We Support",
      sportsKicker: "Disciplines in which Northcrest advises today.",
      familyTitle: "Why Families Trust Us",
      familyItems: [
        {
          t: "Professional Guidance",
          d: "Navigating the pathway to US universities can be complex and overwhelming. Northcrest provides structured, transparent and professional guidance throughout every stage of the journey — from athlete evaluation and university selection to recruitment strategy and application support. We work closely with athletes and families to ensure informed decisions built on clarity, trust and long-term success.",
        },
        {
          t: "International Expertise",
          d: "Northcrest was built on the connection between Switzerland, Portugal and the United States. Our international perspective allows us to understand both the European athlete development environment and the expectations of American universities and coaches. By combining cultural understanding, educational insight and athletic expertise, we help families confidently navigate opportunities across borders.",
        },
        {
          t: "Personalized Strategy",
          d: "Every athlete has a unique story, profile and ambition. At Northcrest, we do not believe in one-size-fits-all solutions. We develop individualized strategies based on athletic ability, academic goals, personal aspirations and long-term development. Our objective is to identify the right pathway for each student-athlete — not simply the fastest one.",
        },
        {
          t: "Long-Term Development",
          d: "Our commitment extends beyond university placement. Northcrest focuses on building strong foundations for long-term academic, athletic and personal success. We aim to support athletes in developing discipline, leadership, confidence and international experience that will benefit them far beyond their years in college sport.",
        },
      ],
      testimonialsTitle: "Voices",
      testimonialsKicker: "Families and athletes on the Northcrest experience.",
      testimonials: [
        {
          q: "Northcrest treated our daughter’s future the way a private bank treats wealth — with discretion, strategy and patience.",
          a: "Parent · Davos",
        },
        {
          q: "The level of preparation and coach outreach was simply on another level. I arrived in the U.S. ready.",
          a: "Soccer Athlete · Porto",
        },
        {
          q: "They are not agents. They are advisors. That difference is everything.",
          a: "Parent · Lisbon",
        },
      ],
      ctaBlockTitle: "Start Your Journey",
      ctaBlockBody:
        "A conversation with Northcrest is a conversation about a future few will ever access. We invite you to begin.",
    },
    about: {
      title: "About Us",
      kicker: "Northcrest Athletics",
      missionT: "Mission",
      missionParas: [
        "Northcrest exists to guide ambitious student-athletes toward life-changing academic and athletic opportunities in the United States.",
        "Through trusted advisory, personalized strategy, and long-term support, we help athletes and their families navigate the complex pathway between potential and opportunity.",
        "Our mission is not simply to place athletes at universities.",
        "Our mission is to help young people build futures defined by education, ambition, character, and personal growth.",
      ],
      visionT: "Vision",
      visionParas: [
        "To become the most trusted international athlete advisory platform connecting Europe and the United States.",
        "Northcrest envisions a future in which talented student-athletes are no longer limited by geography, access, or lack of guidance.",
        "We aspire to build an ecosystem that empowers athletes throughout every stage of their journey — from development and university placement to career growth, mentorship, and lifelong opportunity.",
        "Our long-term vision extends beyond sport.",
        "We aim to create lasting impact through education, international experience, leadership, and human development.",
      ],
      valuesT: "Core Values",
      valuesItems: [
        {
          t: "Trust",
          paras: [
            "Families place their future in our hands.",
            "We operate with honesty, transparency, professionalism, and integrity in every decision we make.",
            "Trust is the foundation of Northcrest.",
          ],
        },
        {
          t: "Excellence",
          paras: [
            "We believe excellence is built through discipline, preparation, consistency, and accountability.",
            "Northcrest pursues the highest standards in both academic and athletic guidance.",
          ],
        },
        {
          t: "Opportunity",
          paras: [
            "Every talented athlete deserves a pathway.",
            "We are committed to opening doors, creating access, and helping ambitious young people realize their full potential.",
          ],
        },
        {
          t: "Long-Term Thinking",
          paras: [
            "We prioritize sustainable development over short-term results.",
            "Our objective is not only successful placements, but successful futures.",
          ],
        },
        {
          t: "Athlete-First Philosophy",
          paras: [
            "Every athlete is unique.",
            "We provide individualized guidance based on personal goals, academic aspirations, athletic ability, and long-term development.",
            "People always come before transactions.",
          ],
        },
        {
          t: "International Perspective",
          paras: [
            "Northcrest was built on the connection between Switzerland, Portugal, and the United States.",
            "We embrace cultural diversity, global thinking, and international opportunity.",
          ],
        },
      ],
      philosophyT: "Northcrest Philosophy",
      philosophyTagline1: "North is the direction.",
      philosophyTagline2: "Crest is the summit.",
      philosophyBody:
        "Northcrest exists to guide ambitious student-athletes toward the highest level of academic and athletic achievement in the United States.",
      philosophyClose: "From Europe to Opportunity.",
      presenceT: "International Presence",
      offices: [
        { city: "Dover", country: "Delaware, USA", role: "Global Headquarters" },
        { city: "Bern", country: "Switzerland", role: "European Headquarters" },
        { city: "Lisbon", country: "Portugal", role: "European Operations" },
        { city: "San Diego", country: "California, USA", role: "U.S. Operations" },
      ],
      networkT: "Founder's Note",
      networkD:
        "A global network of universities, coaches, academic mentors and former student-athletes — assembled with the same care as the families we serve.",
      founderNoteT: "Founder's Note",
      founderNoteParas: [
        "At Northcrest, we believe that talent deserves guidance, opportunity, and a clear pathway forward.",
        "The purpose of our company is not simply to help athletes reach universities, but to help young people unlock their full potential through education, sport, and international experience.",
        "We founded Northcrest to become a trusted bridge between ambition and opportunity — empowering student-athletes and their families to navigate one of the most important journeys of their lives with confidence, clarity, and purpose.",
      ],
    },
    partners: {
      title: "Partners",
      kicker: "A curated network. Built on trust.",
      intro: "Northcrest works alongside select clubs, schools, and institutions across Europe and the United States — partners who share our standards of discretion, excellence, and long-term commitment to student-athletes.",
      sections: [
        {
          slug: "clubs",
          t: "Clubs",
          d: "Northcrest collaborates with select European clubs across multiple disciplines to identify, develop, and prepare ambitious student-athletes for the American collegiate route. Each partnership is built on shared standards and long-term commitment.",
        },
        {
          slug: "schools",
          t: "Schools",
          d: "Working closely with international schools and academic institutions, we ensure that academic excellence and athletic ambition advance in parallel — preparing student-athletes for the most competitive American universities.",
        },
        {
          slug: "institutional",
          t: "Institutional Partnerships",
          d: "A growing network of universities, federations, and institutional partners that share our commitment to long-term athlete development and trusted, structured guidance across borders.",
        },
      ],
      namedPartners: {
        visitCta: "Visit website",
        title: "Announced Partners",
        kicker: "Institutions we work alongside.",
        items: [
          { name: "Sporting Clube de Portugal", role: "Camp Partner", href: "https://www.sporting.pt", logo: "/partners/sporting-cp.png", d: "One of Europe's most respected youth academies — an anchor partner for the Northcrest × Sporting CP Soccer Camps." },
          { name: "Sportrail", role: "Education Partner", href: "https://www.sportrail.pt", logo: "/partners/sportrail.webp", d: "Portugal's leading certified provider of football coach education (DGERT & IPDJ). Since 2016, more than 3,500 coaches trained by an elite faculty of 70+ instructors drawn from top European clubs — the institutional foundation of Northcrest's Coach Education program." },
          { name: "United Portuguese S.E.S.", role: "Community Partner · San Diego", logo: "/partners/upses.webp", href: "https://www.upses.com", d: "Since 1922, the heart of San Diego's Portuguese community in Point Loma — promoting faith, culture, civic life and unity among families. Our community partner for the Northcrest × Sporting CP Camp San Diego." },
          { name: "Arkansas Comets FC", role: "Partner Club", logo: "/partners/comets.png", href: "https://www.arkansascometsfc.com", d: "Founded in 1993 in Springdale, Northwest Arkansas's most successful soccer club — from Comets Juniors to ECNL Boys & Girls (U6–U19), dedicated to maximising every player's potential. Our partner club for the Northcrest × Sporting CP Camp Arkansas." },
        ],
      },
      closing: "Partnerships are formed by introduction. To explore a collaboration with Northcrest, please write to us.",
    },
    services: {
      title: "Services",
      kicker: "A complete advisory across the student-athlete journey.",
      items: [
        { t: "Athlete Assessment", d: "Academic, athletic and personal readiness review." },
        { t: "University Matching", d: "Programs aligned with ambition, fit and outcome." },
        { t: "Recruitment Strategy", d: "Structured campaigns, never improvised." },
        { t: "Coach Outreach", d: "Direct relationships with U.S. coaching staff." },
        { t: "Application Support", d: "Common App, essays, transcripts, exam strategy." },
        { t: "Scholarship Guidance", d: "Athletic, academic and need-based pathways." },
        { t: "Family Advisory", d: "A trusted partner for the parents, not only the athlete." },
        { t: "Career Development", d: "Beyond placement: the long arc of opportunity." },
        { t: "NIL Readiness", d: "Brand, image and Name-Image-Likeness preparation." },
      ],
      featuredT: "Programs",
      featuredKicker: "Curated by Northcrest.",
      featured: [
        { slug: "soccer-camps", t: "Soccer Camps", d: "High-performance soccer camps in the United States, in partnership with Sporting Clube de Portugal.", cta: "Explore Camps" },
        { slug: "coach-education", t: "Coach Education", d: "Professional development programs for coaches — in preparation with our institutional partners.", cta: "Learn More" },
      ],
      futureT: "Forthcoming",
      future: [
        { t: "Northcrest Elite", d: "An invitation-only mentorship circle." },
        { t: "Northcrest Camps", d: "Curated showcases hosted in Europe and the U.S." },
        { t: "Northcrest Alumni", d: "A lifelong network for placed athletes." },
      ],
    },
    camps: {
      title: "Soccer Camps 2027",
      kicker: "In partnership with Sporting Clube de Portugal.",
      intro: "A limited series of high-performance soccer camps hosted in the United States, delivered in partnership with Sporting Clube de Portugal — one of Europe's most respected youth academies. Guided by Sporting-formed coaches, curated for ambitious boys and girls aged 6–17.",
      confirmedLabel: "Confirmed dates",
      tbaLabel: "Additional locations in negotiation",
      tbaBadge: "In confirmation",
      tbaCard: "Location to be announced",
      tbaLocations: ["Oregon", "Massachusetts", "Raleigh, NC", "Texas"],
      ageLabel: "Ages",
      registerCta: "Register",
      openingSoonCta: "Registrations opening soon",
      registerSectionTitle: "Camp Registration",
      registerSectionKicker: "Reserve your place. Places are limited.",
      openingKicker: "Registrations open September 2026",
      openingTitle: "Details to be released soon",
      openingBody: "Venues, dates and pricing for the 2027 camps will be published together with the opening of registrations. Register your interest to be notified as soon as places become available.",
      openingCta: "Register your interest",
      partnershipLabel: "In partnership with",
      feesLabel: "Investment",
      feeTiers: { early: "Early Bird", regular: "Regular", late: "Late Bloomer" },
      feeTierDates: {
        early: "registration until 31 December 2026",
        regular: "registration until 31 March 2027",
        late: "until 1st camp day",
      },
      confirmed: [
        { slug: "san-diego", city: "San Diego, CA", venue: "Point Loma Nazarene University", dateRange: "9–13 June 2027", ages: "6–17", fees: { early: 459, regular: 509, late: 559 }, registerHref: "https://www.sportingcampsandiego.us", partner: { name: "United Portuguese S.E.S.", logo: "/partners/upses.webp", href: "https://www.upses.com" } },
        { slug: "arkansas", city: "Springdale, AR", venue: "Brunner & Lay Soccer Complex", dateRange: "28 June – 2 July 2027", ages: "6–17", fees: { early: 399, regular: 449, late: 499 }, registerHref: "https://www.sportingcamparkansas.us", partner: { name: "Arkansas Comets FC", logo: "/partners/comets.png", href: "https://www.arkansascometsfc.com" } },
        { slug: "artesia", city: "Artesia, CA", venue: "", dateRange: "14–18 June 2027", ages: "6–17" },
        { slug: "sarasota", city: "Sarasota, FL", venue: "", dateRange: "8–11 July 2027", ages: "6–17" },
      ],
      form: {
        athleteSectionTitle: "Athlete",
        firstName: "First Name",
        lastName: "Last Name",
        dob: "Date of Birth",
        sex: "Sex",
        sexOptions: [
          { v: "", l: "—" },
          { v: "male", l: "Male" },
          { v: "female", l: "Female" },
          { v: "other", l: "Prefer not to say" },
        ],
        position: "Position",
        positionOptions: [
          { v: "", l: "—" },
          { v: "goalkeeper", l: "Goalkeeper" },
          { v: "defender", l: "Defender" },
          { v: "midfielder", l: "Midfielder" },
          { v: "forward", l: "Forward" },
        ],
        club: "Current Club",
        guardianSectionTitle: "Guardian",
        guardianName: "Guardian Full Name",
        guardianEmail: "Guardian Email",
        guardianPhone: "Guardian Phone",
        guardianCountry: "Country",
        campSectionTitle: "Camp Selection",
        campLabel: "Choose Camp",
        campPlaceholder: "— Select a camp —",
        addOnsSectionTitle: "Additional Services (optional)",
        addOnsIntro: "All prices in USD. Purchased at registration, delivered on-site during the camp.",
        addOns: {
          individual_training: {
            title: "Individual Training",
            subtitle: "40-minute sessions with a Sporting-formed coach.",
            tiers: [
              { v: "", l: "None", price: null },
              { v: "single", l: "Single sessions", note: "$69 per session — up to 2/day", price: 69, perUnit: true },
              { v: "pack1", l: "Pack — 1 session per camp day", price: 249 },
              { v: "pack2", l: "Pack — 2 sessions per camp day", price: 459 },
            ],
            qtyLabel: "Number of sessions",
          },
          gk_training: {
            title: "Individual Goalkeeper Training",
            subtitle: "40-minute sessions with a specialist goalkeeper coach.",
            tiers: [
              { v: "", l: "None", price: null },
              { v: "single", l: "Single sessions", note: "$69 per session — up to 2/day", price: 69, perUnit: true },
              { v: "pack1", l: "Pack — 1 session per camp day", price: 249 },
              { v: "pack2", l: "Pack — 2 sessions per camp day", price: 459 },
            ],
            qtyLabel: "Number of sessions",
          },
          analyst: {
            title: "Analyst Feedback",
            subtitle: "Individual performance analysis by our analyst. Up to 1 session per camp day.",
            tiers: [
              { v: "", l: "None", price: null },
              { v: "verbal", l: "20-minute verbal session", price: 49, perUnit: true },
              { v: "full", l: "Full analysis + 30-min 1-on-1 + written summary (report delivered on last camp day)", price: 89, perUnit: true },
              { v: "full_video", l: "Full analysis + 30-min 1-on-1 + written summary + personal analysis Video (report and video delivered on last camp day)", price: 119, perUnit: true },
            ],
            qtyLabel: "Number of sessions",
          },
        },
        addOnsTotalLabel: "Estimated total (add-ons)",
        campFeeLabel: "Camp fee",
        grandTotalLabel: "Estimated Total",
        notesLabel: "Notes, medical information or allergies",
        consentLabel: "I confirm I am the athlete's parent or legal guardian and consent to the processing of the information above per the",
        privacyLinkLabel: "Privacy Policy",
        submit: "Submit Registration",
        submitting: "Submitting…",
        success: "Registration received. We will be in touch shortly.",
        error: "Something went wrong. Please review the form and try again.",
        requiredMissing: "Please complete all required fields.",
        consentRequired: "Please confirm parental consent before submitting.",
        registerAnother: "Register another athlete",
      },
    },
    coachEducation: {
      title: "Coach Education",
      kicker: "Programs in preparation. First course open for enrollment.",
      intro: "Northcrest is developing a curated series of professional development programs for coaches — designed with our institutional partners in Europe and the United States. Precise. Deliberate. Uncompromising.",
      featuredLabel: "Enrollment Open",
      browseLabel: "Browse Programs",
      featured: [
        {
          partner: "In collaboration with Sportrail",
          title: "The Spanish Way",
          subtitle: "Youth Development Methodology",
          d: "Six live online sessions on the methodology behind Spain's youth development — game model, positional play, microcycle design and the pathway from the academy to senior football. Delivered by practitioners currently working in elite academy football.",
          facts: [
            { k: "Format", v: "6 × 2h live · online · ~20h CPD" },
            { k: "First cohort", v: "20 Oct → 5 Nov 2026 · 3 weeks" },
            { k: "Investment", v: "$227 early bird · pay in full or 3 monthly instalments" },
            { k: "Cohort cap", v: "40 coaches" },
            { k: "Certification", v: "Sportrail · DGERT & IPDJ certified" },
          ],
          registerCta: "Register on Sportrail",
          registerHref: "https://sportrailfootball.com/courses/spanish-way?ref=northcrest",
        },
        {
          partner: "In collaboration with Sportrail",
          title: "Goalkeeper Coaching",
          subtitle: "The European Approach",
          d: "Six live online sessions for goalkeeper coaches — from building a GK department and microcycle planning to defending the goal, footwork in build-up, youth pathways and scouting frameworks. Taught by practitioners currently working at Newcastle United, Norwich City, Real Salt Lake, Coleraine FC and ex-Marseille/Barcelona.",
          facts: [
            { k: "Format", v: "6 × 2h live · online · 20h CPD" },
            { k: "Next cohort", v: "22 Sep → 8 Oct 2026 · 3 weeks" },
            { k: "Investment", v: "$277 · pay in full or 3 monthly instalments" },
            { k: "Cohort cap", v: "40 coaches" },
            { k: "Certification", v: "Sportrail · DGERT & IPDJ certified" },
          ],
          registerCta: "Register on Sportrail",
          registerHref: "https://sportrailfootball.com/courses/goalkeeper-coaching?ref=northcrest",
        },
        {
          partner: "In collaboration with Sportrail",
          title: "Scouting in Football",
          subtitle: "From Talent to Contract",
          d: "Six live online sessions on the craft of football scouting — concepts and myths, the scout's role inside a club, elite Premier League environments, geostrategy, data and technology, and reports that decision-makers act on. Taught by João Ferreira, International First-Team Scout at Tottenham Hotspur.",
          facts: [
            { k: "Format", v: "6 × 2h live · online · 20h CPD" },
            { k: "Next cohort", v: "15 Sep → 16 Oct 2026" },
            { k: "Investment", v: "$227 early bird · pay in full or 3 monthly instalments" },
            { k: "Cohort cap", v: "40 coaches" },
            { k: "Certification", v: "Sportrail · DGERT & IPDJ certified" },
          ],
          registerCta: "Register on Sportrail",
          registerHref: "https://sportrailfootball.com/courses/scouting?ref=northcrest",
        },
      ],
      upcomingLabel: "Also in development",
      upcoming: [
        { t: "Goalkeeper Coach Formation", d: "A structured curriculum in the modern principles of goalkeeper development." },
        { t: "Training Methodology", d: "The design, structure and periodisation behind high-performance programs." },
        { t: "Further Programs", d: "Additional formations across positional, physical, and tactical disciplines." },
      ],
      closing: "To be notified when the next programs open, please write to us.",
      contactCta: "Register your interest",
    },
    sports: {
      title: "Sports We Support",
      kicker: "Disciplines in which Northcrest advises today.",
      list: [
        { slug: "soccer", t: "Soccer" },
        { slug: "tennis", t: "Tennis" },
        { slug: "golf", t: "Golf" },
      ],
      futureT: "Other Sports",
      futureD: "Any other sport will be assessed on a case-by-case basis.",
      detail: {
        overview: "Overview",
        opportunities: "Opportunities",
        pathways: "University Pathways",
        timeline: "Recruitment Timeline",
        faqs: "Frequently Asked Questions",
        timelineSteps: [
          { y: "Year 1", t: "Foundation", d: "Academic mapping, athletic profile, first film." },
          { y: "Year 2", t: "Positioning", d: "Showcases, exam strategy, longlist of programs." },
          { y: "Year 3", t: "Outreach", d: "Coach communication, official visits, shortlist." },
          { y: "Year 4", t: "Placement", d: "Offers, signing, transition planning." },
        ],
      },
    },
    universities: {
      title: "Universities",
      kicker: "An interactive view of the American collegiate landscape.",
      filterAll: "All Divisions",
      categories: ["NCAA Division I", "NCAA Division II", "NCAA Division III", "NAIA", "NJCAA"],
      searchPlaceholder: "Search by state, university or division…",
      hintMap: "Select a state to view representative programs.",
      noResults: "No programs match this selection.",
    },
    success: {
      title: "Success Stories",
      kicker: "A measured account of athletes we have placed.",
      placeholder:
        "Curated case studies will be added here as families and athletes consent to share their journey. Discretion is, and will remain, our first principle.",
    },
    resources: {
      title: "Resources",
      kicker: "Guides and reading for families navigating the path.",
      categories: ["Blog", "Guides", "Scholarship Information", "NCAA Overview", "Recruitment Timeline", "Parent Resources", "Student-Athlete Checklist"],
      items: [
        { c: "NCAA Overview", t: "Understanding Divisions I, II, III", d: "How U.S. collegiate athletics is structured — and why it matters for European families." },
        { c: "Scholarship Information", t: "The Truth About Athletic Scholarships", d: "A measured guide. Beyond the myths, beyond the marketing." },
        { c: "Recruitment Timeline", t: "A Four-Year Recruitment Calendar", d: "What to do, and when — from sophomore year to signing." },
        { c: "Parent Resources", t: "A Letter to Parents", d: "What we ask of families. And what families should ask of us." },
        { c: "Guides", t: "Student-Athlete Checklist", d: "An institutional standard for organising the journey." },
        { c: "Blog", t: "From Europe to Opportunity", d: "Field notes from the Northcrest advisory desk." },
      ],
    },
    contact: {
      title: "Begin a Conversation",
      kicker: "Consultation by invitation. We respond personally.",
      name: "Full Name",
      email: "Email Address",
      phone: "Phone",
      country: "Country",
      sport: "Sport",
      year: "Graduation Year",
      message: "How can we help?",
      submit: "Request Consultation",
      success: "Thank you. A Northcrest advisor will be in touch.",
      error: "Something went wrong. Please try again or write to us directly.",
      calendlyT: "Or schedule directly",
      calendlyD: "Calendly integration available upon request.",
      mapsT: "Visit Us",
      phonesT: "Direct Lines",
      offices: [
        { city: "Bern, Switzerland", role: "Headquarters" },
        { city: "San Diego, California", role: "U.S. Operations" },
      ],
    },
    footer: {
      brandLine: "Premium athlete advisory. Switzerland · Portugal · United States.",
      programs: "Programs",
      company: "Company",
      discover: "Discover",
      explore: "Explore",
      offices: "Offices",
      legal: "Legal",
      privacy: "Privacy",
      terms: "Terms",
      cookies: "Cookies",
      rights: "All rights reserved.",
      newsletterTitle: "Updates from Northcrest",
      newsletterKicker: "New camps, courses and partnership announcements — occasionally, never spam.",
      newsletterPlaceholder: "Your email",
      newsletterCta: "Subscribe",
      newsletterSuccess: "Thank you. We'll be in touch.",
      newsletterError: "Something went wrong. Please try again.",
      newsletterInvalid: "Please enter a valid email address.",
    },
  },

  de: {
    nav: {
      home: "Start",
      about: "Über Uns",
      camps: "Camps",
      coachEd: "Trainerausbildung",
      coachEdShort: "Ausbildung",
      partners: "Partner",
      legal: "Rechtliches",
      contact: "Kontakt",
      universities: "Universitäten",
      sports: "Sportarten",
      successStories: "Erfolgsgeschichten",
      resources: "Wissen",
      bookCta: "Beratung buchen",
    },
    common: {
      learnMore: "Mehr erfahren",
      readMore: "Weiterlesen",
      explore: "Entdecken",
      back: "Zurück",
      contactUs: "Kontakt",
      sendMessage: "Nachricht senden",
      submitting: "Wird gesendet…",
      ourOffices: "Unsere Standorte",
      tagline: "North ist die Richtung. Crest ist der Gipfel.",
      taglineSecondary: "Von Europa zu neuen Horizonten.",
      seeAll: "Alle ansehen",
    },
    home: {
      heroOverline: "Exklusive Athletenberatung · Delaware · Bern · Lissabon · San Diego",
      heroTitle1: "Dein Potenzial.",
      heroTitle2: "Deine Zukunft.",
      heroSubtitle:
        "Wir begleiten ambitionierte Studenten-Athleten zu akademischen und sportlichen Möglichkeiten an US-Universitäten.",
      ctaPrimary: "Beratung buchen",
      ctaSecondary: "Mehr erfahren",
      whyTitle: "Warum Northcrest",
      whyKicker: "Drei Säulen",
      pillars: [
        { k: "Akademische Exzellenz", d: "Strenge akademische Vorbereitung und Universitätsplatzierung." },
        { k: "Sportliche Leistung", d: "Leistungsorientierte Entwicklung und direkter Trainerkontakt." },
        { k: "Internationale Möglichkeiten", d: "Eine vertrauensvolle Brücke von Europa nach Amerika." },
      ],
      processTitle: "Unser Prozess",
      processKicker: "Ein abgemessener Weg. Sechs bewusste Schritte.",
      processSteps: [
        { n: "01", t: "Analyse", d: "Vollständige akademische und sportliche Profilbewertung." },
        { n: "02", t: "Strategie", d: "Ein maßgeschneiderter Beratungsplan." },
        { n: "03", t: "Universitäts-Matching", d: "Kuratierte Programme in NCAA, NAIA und NJCAA." },
        { n: "04", t: "Trainerkontakt", d: "Direkte Vorstellung bei US-Trainern." },
        { n: "05", t: "Platzierung", d: "Bewerbung, Stipendien und Angebote." },
        { n: "06", t: "Erfolg", d: "Langfristige Mentorschaft." },
      ],
      sportsTitle: "Unterstützte Sportarten",
      sportsKicker: "Disziplinen, in denen Northcrest berät.",
      familyTitle: "Warum Familien Uns Vertrauen",
      familyItems: [
        {
          t: "Professionelle Beratung",
          d: "Der Weg zu US-Universitäten kann komplex und überwältigend sein. Northcrest bietet eine strukturierte, transparente und professionelle Beratung in jeder Phase der Reise — von der Athletenbewertung und Universitätsauswahl bis hin zur Rekrutierungsstrategie und Bewerbungsunterstützung. Wir arbeiten eng mit Athleten und Familien zusammen, um fundierte Entscheidungen auf der Grundlage von Klarheit, Vertrauen und langfristigem Erfolg zu treffen.",
        },
        {
          t: "Internationale Expertise",
          d: "Northcrest wurde auf der Verbindung zwischen der Schweiz, Portugal und den Vereinigten Staaten aufgebaut. Unsere internationale Perspektive ermöglicht es uns, sowohl das europäische Umfeld der Athletenentwicklung als auch die Erwartungen amerikanischer Universitäten und Trainer zu verstehen. Durch die Kombination von kulturellem Verständnis, pädagogischem Einblick und sportlicher Expertise helfen wir Familien, Möglichkeiten über Grenzen hinweg selbstbewusst zu navigieren.",
        },
        {
          t: "Persönliche Strategie",
          d: "Jeder Athlet hat eine einzigartige Geschichte, ein Profil und eine Ambition. Bei Northcrest glauben wir nicht an Einheitslösungen. Wir entwickeln individuelle Strategien basierend auf sportlichen Fähigkeiten, akademischen Zielen, persönlichen Bestrebungen und langfristiger Entwicklung. Unser Ziel ist es, den richtigen Weg für jeden Studenten-Athleten zu identifizieren — nicht einfach den schnellsten.",
        },
        {
          t: "Langfristige Entwicklung",
          d: "Unser Engagement geht über die Universitätsplatzierung hinaus. Northcrest konzentriert sich auf den Aufbau starker Grundlagen für langfristigen akademischen, sportlichen und persönlichen Erfolg. Wir möchten Athleten dabei unterstützen, Disziplin, Führung, Selbstvertrauen und internationale Erfahrung zu entwickeln, die ihnen weit über ihre Jahre im College-Sport hinaus zugutekommen.",
        },
      ],
      testimonialsTitle: "Stimmen",
      testimonialsKicker: "Familien und Athleten über Northcrest.",
      testimonials: [
        { q: "Northcrest behandelt die Zukunft unserer Tochter wie eine Privatbank Vermögen behandelt.", a: "Eltern · Davos" },
        { q: "Die Vorbereitung und der Trainerkontakt waren auf einem anderen Niveau.", a: "Fußballathlet · Porto" },
        { q: "Sie sind keine Agenten. Sie sind Berater. Dieser Unterschied ist alles.", a: "Eltern · Lissabon" },
      ],
      ctaBlockTitle: "Beginne deine Reise",
      ctaBlockBody:
        "Ein Gespräch mit Northcrest ist ein Gespräch über eine Zukunft, die wenige je erleben. Wir laden dich ein.",
    },
    about: {
      title: "Über Uns",
      kicker: "Northcrest Athletics",
      missionT: "Mission",
      missionParas: [
        "Northcrest existiert, um ambitionierte Studenten-Athleten zu lebensverändernden akademischen und sportlichen Möglichkeiten in den Vereinigten Staaten zu führen.",
        "Durch vertrauensvolle Beratung, persönliche Strategie und langfristige Unterstützung helfen wir Athleten und ihren Familien, den komplexen Weg zwischen Potenzial und Möglichkeit zu navigieren.",
        "Unsere Mission ist nicht einfach, Athleten an Universitäten zu platzieren.",
        "Unsere Mission ist, jungen Menschen zu helfen, eine Zukunft aufzubauen, die durch Bildung, Ambition, Charakter und persönliches Wachstum geprägt ist.",
      ],
      visionT: "Vision",
      visionParas: [
        "Die vertrauenswürdigste internationale Athletenberatungsplattform zu werden, die Europa und die Vereinigten Staaten verbindet.",
        "Northcrest stellt sich eine Zukunft vor, in der talentierte Studenten-Athleten nicht mehr durch Geografie, Zugang oder fehlende Beratung eingeschränkt sind.",
        "Wir streben danach, ein Ökosystem aufzubauen, das Athleten in jeder Phase ihrer Reise stärkt — von der Entwicklung und Universitätsplatzierung bis hin zu Karrierewachstum, Mentorenschaft und lebenslangen Möglichkeiten.",
        "Unsere langfristige Vision reicht über den Sport hinaus.",
        "Wir wollen nachhaltige Wirkung durch Bildung, internationale Erfahrung, Führung und menschliche Entwicklung schaffen.",
      ],
      valuesT: "Kernwerte",
      valuesItems: [
        {
          t: "Vertrauen",
          paras: [
            "Familien legen ihre Zukunft in unsere Hände.",
            "Wir handeln mit Ehrlichkeit, Transparenz, Professionalität und Integrität in jeder Entscheidung.",
            "Vertrauen ist das Fundament von Northcrest.",
          ],
        },
        {
          t: "Exzellenz",
          paras: [
            "Wir glauben, dass Exzellenz durch Disziplin, Vorbereitung, Beständigkeit und Verantwortung entsteht.",
            "Northcrest verfolgt höchste Standards in der akademischen und sportlichen Beratung.",
          ],
        },
        {
          t: "Möglichkeit",
          paras: [
            "Jeder talentierte Athlet verdient einen Weg.",
            "Wir engagieren uns dafür, Türen zu öffnen, Zugang zu schaffen und ambitionierten jungen Menschen zu helfen, ihr volles Potenzial zu entfalten.",
          ],
        },
        {
          t: "Langfristiges Denken",
          paras: [
            "Wir priorisieren nachhaltige Entwicklung gegenüber kurzfristigen Ergebnissen.",
            "Unser Ziel sind nicht nur erfolgreiche Platzierungen, sondern erfolgreiche Zukünfte.",
          ],
        },
        {
          t: "Athletenorientierte Philosophie",
          paras: [
            "Jeder Athlet ist einzigartig.",
            "Wir bieten individuelle Beratung basierend auf persönlichen Zielen, akademischen Bestrebungen, sportlichen Fähigkeiten und langfristiger Entwicklung.",
            "Menschen kommen immer vor Transaktionen.",
          ],
        },
        {
          t: "Internationale Perspektive",
          paras: [
            "Northcrest wurde auf der Verbindung zwischen der Schweiz, Portugal und den Vereinigten Staaten aufgebaut.",
            "Wir umarmen kulturelle Vielfalt, globales Denken und internationale Möglichkeiten.",
          ],
        },
      ],
      philosophyT: "Northcrest Philosophie",
      philosophyTagline1: "North ist die Richtung.",
      philosophyTagline2: "Crest ist der Gipfel.",
      philosophyBody:
        "Northcrest existiert, um ambitionierte Studenten-Athleten zum höchsten Niveau akademischer und sportlicher Leistung in den Vereinigten Staaten zu führen.",
      philosophyClose: "Von Europa zu neuen Horizonten.",
      presenceT: "Internationale Präsenz",
      offices: [
        { city: "Dover", country: "Delaware, USA", role: "Globaler Hauptsitz" },
        { city: "Bern", country: "Schweiz", role: "Europäischer Hauptsitz" },
        { city: "Lissabon", country: "Portugal", role: "Europäische Geschäftsstelle" },
        { city: "San Diego", country: "Kalifornien, USA", role: "US-Geschäftsstelle" },
      ],
      networkT: "Vom Gründer",
      networkD:
        "Ein globales Netzwerk aus Universitäten, Trainern, akademischen Mentoren und ehemaligen Athleten.",
      founderNoteT: "Vom Gründer",
      founderNoteParas: [
        "Bei Northcrest glauben wir, dass Talent Beratung, Perspektive und einen klaren Weg nach vorn verdient.",
        "Der Zweck unseres Unternehmens ist nicht einfach, Athleten zu Universitäten zu führen, sondern jungen Menschen zu helfen, ihr volles Potenzial durch Bildung, Sport und internationale Erfahrung zu entfalten.",
        "Wir haben Northcrest gegründet, um eine vertrauensvolle Brücke zwischen Ambition und Möglichkeit zu werden — und Studenten-Athleten und ihren Familien zu ermöglichen, eine der wichtigsten Reisen ihres Lebens mit Zuversicht, Klarheit und Sinn zu navigieren.",
      ],
    },
    partners: {
      title: "Partner",
      kicker: "Ein kuratiertes Netzwerk. Auf Vertrauen gebaut.",
      intro: "Northcrest arbeitet mit ausgewählten Vereinen, Schulen und Institutionen in Europa und den Vereinigten Staaten zusammen — Partnern, die unsere Standards von Diskretion, Exzellenz und langfristigem Engagement für Studenten-Athleten teilen.",
      sections: [
        {
          slug: "clubs",
          t: "Vereine",
          d: "Northcrest arbeitet mit ausgewählten europäischen Vereinen in mehreren Disziplinen zusammen, um ambitionierte Studenten-Athleten für den amerikanischen College-Weg zu identifizieren, zu entwickeln und vorzubereiten. Jede Partnerschaft basiert auf gemeinsamen Standards und langfristigem Engagement.",
        },
        {
          slug: "schools",
          t: "Schulen",
          d: "In enger Zusammenarbeit mit internationalen Schulen und akademischen Institutionen stellen wir sicher, dass akademische Exzellenz und sportlicher Ehrgeiz parallel voranschreiten — und bereiten Studenten-Athleten auf die wettbewerbsfähigsten amerikanischen Universitäten vor.",
        },
        {
          slug: "institutional",
          t: "Institutionelle Partnerschaften",
          d: "Ein wachsendes Netzwerk aus Universitäten, Verbänden und institutionellen Partnern, die unser Engagement für die langfristige Athletenentwicklung und eine vertrauensvolle, strukturierte Begleitung über Grenzen hinweg teilen.",
        },
      ],
      namedPartners: {
        visitCta: "Website besuchen",
        title: "Bekannt gegebene Partner",
        kicker: "Institutionen, mit denen wir zusammenarbeiten.",
        items: [
          { name: "Sporting Clube de Portugal", role: "Camp-Partner", href: "https://www.sporting.pt", logo: "/partners/sporting-cp.png", d: "Eine der angesehensten Jugendakademien Europas — Ankerpartner der Northcrest × Sporting CP Soccer Camps." },
          { name: "Sportrail", role: "Bildungspartner", href: "https://www.sportrail.pt", logo: "/partners/sportrail.webp", d: "Portugals führender zertifizierter Anbieter für Fußball-Trainerausbildung (DGERT & IPDJ). Seit 2016 wurden über 3.500 Trainer durch ein Elite-Kollegium aus 70+ Ausbildern aus europäischen Spitzenclubs ausgebildet — das institutionelle Fundament des Northcrest Coach Education Programms." },
          { name: "United Portuguese S.E.S.", role: "Community-Partner · San Diego", logo: "/partners/upses.webp", href: "https://www.upses.com", d: "Seit 1922 das Herz der portugiesischen Gemeinschaft San Diegos in Point Loma — für Glaube, Kultur, bürgerschaftliches Engagement und Zusammenhalt der Familien. Unser Community-Partner für das Northcrest × Sporting CP Camp San Diego." },
          { name: "Arkansas Comets FC", role: "Partnerverein", logo: "/partners/comets.png", href: "https://www.arkansascometsfc.com", d: "1993 in Springdale gegründet und der erfolgreichste Fußballverein im Nordwesten Arkansas — von den Comets Juniors bis zur ECNL für Jungen und Mädchen (U6–U19), mit dem Ziel, das Potenzial jedes Spielers voll auszuschöpfen. Unser Partnerverein für das Northcrest × Sporting CP Camp Arkansas." },
        ],
      },
      closing: "Partnerschaften entstehen auf Einladung. Um eine Zusammenarbeit mit Northcrest zu erkunden, schreibe uns bitte.",
    },
    services: {
      title: "Leistungen",
      kicker: "Eine vollständige Beratung entlang der Athletenreise.",
      items: [
        { t: "Athletenanalyse", d: "Akademische, sportliche und persönliche Bewertung." },
        { t: "Universitäts-Matching", d: "Programme passend zu Ambition und Eignung." },
        { t: "Rekrutierungsstrategie", d: "Strukturierte Kampagnen, nie improvisiert." },
        { t: "Trainerkontakt", d: "Direkte Beziehungen zu US-Trainerstäben." },
        { t: "Bewerbungsbetreuung", d: "Common App, Essays, Zeugnisse, Prüfungsstrategie." },
        { t: "Stipendienberatung", d: "Sportliche, akademische und bedarfsbezogene Wege." },
        { t: "Familienberatung", d: "Ein vertrauter Partner für die Eltern." },
        { t: "Karriereentwicklung", d: "Über die Platzierung hinaus." },
        { t: "NIL-Vorbereitung", d: "Marke, Image, Name-Image-Likeness." },
      ],
      featuredT: "Programme",
      featuredKicker: "Von Northcrest kuratiert.",
      featured: [
        { slug: "soccer-camps", t: "Soccer Camps", d: "High-Performance-Fußballcamps in den USA, in Partnerschaft mit Sporting Clube de Portugal.", cta: "Camps entdecken" },
        { slug: "coach-education", t: "Trainerausbildung", d: "Programme zur professionellen Weiterbildung für Trainer — in Vorbereitung mit unseren institutionellen Partnern.", cta: "Mehr erfahren" },
      ],
      futureT: "Demnächst",
      future: [
        { t: "Northcrest Elite", d: "Ein exklusiver Mentorenkreis." },
        { t: "Northcrest Camps", d: "Kuratierte Showcases in Europa und den USA." },
        { t: "Northcrest Alumni", d: "Ein lebenslanges Netzwerk." },
      ],
    },
    camps: {
      title: "Soccer Camps 2027",
      kicker: "In Partnerschaft mit Sporting Clube de Portugal.",
      intro: "Eine limitierte Serie hochwertiger Fußballcamps in den USA, durchgeführt in Partnerschaft mit Sporting Clube de Portugal — einer der angesehensten Jugendakademien Europas. Betreut von Sporting-geprägten Trainern, kuratiert für ambitionierte Jungen und Mädchen im Alter von 6 bis 17 Jahren.",
      confirmedLabel: "Bestätigte Termine",
      tbaLabel: "Weitere Standorte in Verhandlung",
      tbaBadge: "In Bestätigung",
      tbaCard: "Standort wird bekannt gegeben",
      tbaLocations: ["Oregon", "Massachusetts", "Raleigh, NC", "Texas"],
      ageLabel: "Alter",
      registerCta: "Anmelden",
      openingSoonCta: "Anmeldungen öffnen bald",
      registerSectionTitle: "Camp-Anmeldung",
      registerSectionKicker: "Sichere dir deinen Platz. Plätze sind limitiert.",
      openingKicker: "Anmeldungen öffnen im September 2026",
      openingTitle: "Details werden in Kürze veröffentlicht",
      openingBody: "Standorte, Termine und Preise für die Camps 2027 werden zusammen mit der Öffnung der Anmeldungen bekannt gegeben. Bekunde jetzt dein Interesse, um benachrichtigt zu werden, sobald Plätze verfügbar sind.",
      openingCta: "Interesse bekunden",
      partnershipLabel: "In Partnerschaft mit",
      feesLabel: "Investition",
      feeTiers: { early: "Early Bird", regular: "Regulär", late: "Late Bloomer" },
      feeTierDates: {
        early: "Anmeldung bis 31. Dezember 2026",
        regular: "Anmeldung bis 31. März 2027",
        late: "bis zum ersten Camp-Tag",
      },
      confirmed: [
        { slug: "san-diego", city: "San Diego, CA", venue: "Point Loma Nazarene University", dateRange: "9.–13. Juni 2027", ages: "6–17", fees: { early: 459, regular: 509, late: 559 }, registerHref: "https://www.sportingcampsandiego.us", partner: { name: "United Portuguese S.E.S.", logo: "/partners/upses.webp", href: "https://www.upses.com" } },
        { slug: "arkansas", city: "Springdale, AR", venue: "Brunner & Lay Soccer Complex", dateRange: "28. Juni – 2. Juli 2027", ages: "6–17", fees: { early: 399, regular: 449, late: 499 }, registerHref: "https://www.sportingcamparkansas.us", partner: { name: "Arkansas Comets FC", logo: "/partners/comets.png", href: "https://www.arkansascometsfc.com" } },
        { slug: "artesia", city: "Artesia, CA", venue: "", dateRange: "14.–18. Juni 2027", ages: "6–17" },
        { slug: "sarasota", city: "Sarasota, FL", venue: "", dateRange: "8.–11. Juli 2027", ages: "6–17" },
      ],
      form: {
        athleteSectionTitle: "Athlet",
        firstName: "Vorname",
        lastName: "Nachname",
        dob: "Geburtsdatum",
        sex: "Geschlecht",
        sexOptions: [
          { v: "", l: "—" },
          { v: "male", l: "Männlich" },
          { v: "female", l: "Weiblich" },
          { v: "other", l: "Keine Angabe" },
        ],
        position: "Position",
        positionOptions: [
          { v: "", l: "—" },
          { v: "goalkeeper", l: "Torwart" },
          { v: "defender", l: "Verteidiger" },
          { v: "midfielder", l: "Mittelfeldspieler" },
          { v: "forward", l: "Stürmer" },
        ],
        club: "Aktueller Verein",
        guardianSectionTitle: "Erziehungsberechtigter",
        guardianName: "Vollständiger Name",
        guardianEmail: "E-Mail",
        guardianPhone: "Telefon",
        guardianCountry: "Land",
        campSectionTitle: "Camp-Auswahl",
        campLabel: "Camp wählen",
        campPlaceholder: "— Camp auswählen —",
        addOnsSectionTitle: "Zusätzliche Leistungen (optional)",
        addOnsIntro: "Alle Preise in USD. Bei der Anmeldung gebucht, während des Camps vor Ort erbracht.",
        addOns: {
          individual_training: {
            title: "Individualtraining",
            subtitle: "40-Minuten-Sessions mit einem Sporting-geprägten Trainer.",
            tiers: [
              { v: "", l: "Keine", price: null },
              { v: "single", l: "Einzelsessions", note: "$69 pro Session — bis zu 2/Tag", price: 69, perUnit: true },
              { v: "pack1", l: "Paket — 1 Session pro Camp-Tag", price: 249 },
              { v: "pack2", l: "Paket — 2 Sessions pro Camp-Tag", price: 459 },
            ],
            qtyLabel: "Anzahl der Sessions",
          },
          gk_training: {
            title: "Individuelles Torwarttraining",
            subtitle: "40-Minuten-Sessions mit einem Torwart-Spezialtrainer.",
            tiers: [
              { v: "", l: "Keine", price: null },
              { v: "single", l: "Einzelsessions", note: "$69 pro Session — bis zu 2/Tag", price: 69, perUnit: true },
              { v: "pack1", l: "Paket — 1 Session pro Camp-Tag", price: 249 },
              { v: "pack2", l: "Paket — 2 Sessions pro Camp-Tag", price: 459 },
            ],
            qtyLabel: "Anzahl der Sessions",
          },
          analyst: {
            title: "Analysten-Feedback",
            subtitle: "Individuelle Leistungsanalyse durch unseren Analysten. Bis zu 1 Session pro Camp-Tag.",
            tiers: [
              { v: "", l: "Keine", price: null },
              { v: "verbal", l: "20-minütige mündliche Session", price: 49, perUnit: true },
              { v: "full", l: "Vollständige Analyse + 30-min 1-on-1 + schriftliche Zusammenfassung (Bericht am letzten Camp-Tag)", price: 89, perUnit: true },
              { v: "full_video", l: "Vollständige Analyse + 30-min 1-on-1 + schriftliche Zusammenfassung + persönliches Analyse-Video (Bericht und Video am letzten Camp-Tag)", price: 119, perUnit: true },
            ],
            qtyLabel: "Anzahl der Sessions",
          },
        },
        addOnsTotalLabel: "Geschätzte Summe (Zusatzleistungen)",
        campFeeLabel: "Camp-Gebühr",
        grandTotalLabel: "Geschätzte Gesamtsumme",
        notesLabel: "Anmerkungen, medizinische Informationen oder Allergien",
        consentLabel: "Ich bestätige, Elternteil oder Erziehungsberechtigter des Athleten zu sein, und stimme der Verarbeitung der oben genannten Daten gemäß der",
        privacyLinkLabel: "Datenschutzerklärung",
        submit: "Anmeldung absenden",
        submitting: "Wird gesendet…",
        success: "Anmeldung eingegangen. Wir melden uns in Kürze.",
        error: "Etwas ist schiefgelaufen. Bitte überprüfe das Formular.",
        requiredMissing: "Bitte alle Pflichtfelder ausfüllen.",
        consentRequired: "Bitte bestätige die elterliche Zustimmung.",
        registerAnother: "Weiteren Athleten anmelden",
      },
    },
    coachEducation: {
      title: "Trainerausbildung",
      kicker: "Programme in Vorbereitung. Erster Kurs zur Anmeldung geöffnet.",
      intro: "Northcrest entwickelt eine kuratierte Serie professioneller Weiterbildungsprogramme für Trainer — konzipiert mit unseren institutionellen Partnern in Europa und den USA. Präzise. Bewusst. Kompromisslos.",
      featuredLabel: "Anmeldung geöffnet",
      browseLabel: "Programme durchsuchen",
      featured: [
        {
          partner: "In Zusammenarbeit mit Sportrail",
          title: "The Spanish Way",
          subtitle: "Methodik der Nachwuchsförderung",
          d: "Sechs Live-Online-Sessions zur Methodik hinter Spaniens Nachwuchsförderung — Spielmodell, Positionsspiel, Mikrozyklus-Design und der Weg von der Akademie in den Senioren-Fußball. Vermittelt von Praktikern aus dem elitären Akademie-Fußball.",
          facts: [
            { k: "Format", v: "6 × 2h live · online · ~20h CPD" },
            { k: "Erste Kohorte", v: "20. Okt → 5. Nov 2026 · 3 Wochen" },
            { k: "Investition", v: "$227 Early-Bird · Einmalzahlung oder 3 Monatsraten" },
            { k: "Kohorten-Limit", v: "40 Trainer" },
            { k: "Zertifizierung", v: "Sportrail · DGERT & IPDJ zertifiziert" },
          ],
          registerCta: "Auf Sportrail anmelden",
          registerHref: "https://sportrailfootball.com/courses/spanish-way?ref=northcrest",
        },
        {
          partner: "In Zusammenarbeit mit Sportrail",
          title: "Goalkeeper Coaching",
          subtitle: "The European Approach",
          d: "Sechs Live-Online-Sessions für Torwarttrainer — vom Aufbau einer GK-Abteilung und Mikrozyklus-Planung über Torverteidigung, Fußarbeit im Spielaufbau bis zu Nachwuchspfaden und Scouting-Frameworks. Vermittelt von Praktikern aus Newcastle United, Norwich City, Real Salt Lake, Coleraine FC und ex-Marseille/Barcelona.",
          facts: [
            { k: "Format", v: "6 × 2h live · online · 20h CPD" },
            { k: "Nächste Kohorte", v: "22. Sep → 8. Okt 2026 · 3 Wochen" },
            { k: "Investition", v: "$277 · Einmalzahlung oder 3 Monatsraten" },
            { k: "Kohorten-Limit", v: "40 Trainer" },
            { k: "Zertifizierung", v: "Sportrail · DGERT & IPDJ zertifiziert" },
          ],
          registerCta: "Auf Sportrail anmelden",
          registerHref: "https://sportrailfootball.com/courses/goalkeeper-coaching?ref=northcrest",
        },
        {
          partner: "In Zusammenarbeit mit Sportrail",
          title: "Scouting in Football",
          subtitle: "From Talent to Contract",
          d: "Sechs Live-Online-Sessions zum Handwerk des Fußball-Scoutings — Konzepte und Mythen, die Rolle des Scouts im Club, elitäre Premier-League-Umgebungen, Geostrategie, Daten und Technologie sowie Berichte, auf die Entscheidungsträger reagieren. Vermittelt von João Ferreira, International First-Team Scout bei Tottenham Hotspur.",
          facts: [
            { k: "Format", v: "6 × 2h live · online · 20h CPD" },
            { k: "Nächste Kohorte", v: "15. Sep → 16. Okt 2026" },
            { k: "Investition", v: "$227 Early-Bird · Einmalzahlung oder 3 Monatsraten" },
            { k: "Kohorten-Limit", v: "40 Trainer" },
            { k: "Zertifizierung", v: "Sportrail · DGERT & IPDJ zertifiziert" },
          ],
          registerCta: "Auf Sportrail anmelden",
          registerHref: "https://sportrailfootball.com/courses/scouting?ref=northcrest",
        },
      ],
      upcomingLabel: "Ebenfalls in Entwicklung",
      upcoming: [
        { t: "Torwarttrainer-Ausbildung", d: "Ein strukturiertes Curriculum zu den modernen Prinzipien der Torwartentwicklung." },
        { t: "Trainingsmethodik", d: "Design, Struktur und Periodisierung leistungsstarker Programme." },
        { t: "Weitere Programme", d: "Zusätzliche Ausbildungen in positionalen, physischen und taktischen Disziplinen." },
      ],
      closing: "Um benachrichtigt zu werden, sobald die nächsten Programme öffnen, schreibe uns bitte.",
      contactCta: "Interesse bekunden",
    },
    sports: {
      title: "Unterstützte Sportarten",
      kicker: "Disziplinen, in denen Northcrest heute berät.",
      list: [
        { slug: "soccer", t: "Fußball" },
        { slug: "tennis", t: "Tennis" },
        { slug: "golf", t: "Golf" },
      ],
      futureT: "Weitere Sportarten",
      futureD: "Jede andere Sportart wird im Einzelfall geprüft.",
      detail: {
        overview: "Überblick",
        opportunities: "Möglichkeiten",
        pathways: "Universitätswege",
        timeline: "Rekrutierungsplan",
        faqs: "Häufige Fragen",
        timelineSteps: [
          { y: "Jahr 1", t: "Fundament", d: "Akademisches Mapping, Athletenprofil, erste Highlights." },
          { y: "Jahr 2", t: "Positionierung", d: "Showcases, Prüfungsstrategie, Programmauswahl." },
          { y: "Jahr 3", t: "Trainerkontakt", d: "Kommunikation mit Trainern, Besuche, Shortlist." },
          { y: "Jahr 4", t: "Platzierung", d: "Angebote, Unterzeichnung, Übergang." },
        ],
      },
    },
    universities: {
      title: "Universitäten",
      kicker: "Eine interaktive Ansicht der amerikanischen Hochschullandschaft.",
      filterAll: "Alle Divisionen",
      categories: ["NCAA Division I", "NCAA Division II", "NCAA Division III", "NAIA", "NJCAA"],
      searchPlaceholder: "Nach Staat, Universität oder Division suchen…",
      hintMap: "Wähle einen Staat, um Programme anzuzeigen.",
      noResults: "Keine Programme gefunden.",
    },
    success: {
      title: "Erfolgsgeschichten",
      kicker: "Ein abgemessener Bericht über platzierte Athleten.",
      placeholder:
        "Kuratierte Fallstudien werden hier hinzugefügt, sobald Familien und Athleten zustimmen. Diskretion bleibt unser oberstes Prinzip.",
    },
    resources: {
      title: "Ressourcen",
      kicker: "Leitfäden für Familien auf dem Weg.",
      categories: ["Blog", "Leitfäden", "Stipendien", "NCAA-Überblick", "Zeitachse", "Eltern", "Checkliste"],
      items: [
        { c: "NCAA-Überblick", t: "Divisionen I, II, III verstehen", d: "Wie der US-College-Sport strukturiert ist." },
        { c: "Stipendien", t: "Die Wahrheit über Sportstipendien", d: "Ein abgemessener Leitfaden." },
        { c: "Zeitachse", t: "Ein vierjähriger Recruiting-Kalender", d: "Was zu tun ist, und wann." },
        { c: "Eltern", t: "Ein Brief an die Eltern", d: "Was wir von Familien erbitten." },
        { c: "Leitfäden", t: "Athleten-Checkliste", d: "Institutioneller Standard zur Organisation." },
        { c: "Blog", t: "Von Europa zu neuen Horizonten", d: "Notizen vom Beratungstisch." },
      ],
    },
    contact: {
      title: "Beginne ein Gespräch",
      kicker: "Beratung auf Einladung. Wir antworten persönlich.",
      name: "Vollständiger Name",
      email: "E-Mail-Adresse",
      phone: "Telefon",
      country: "Land",
      sport: "Sportart",
      year: "Abschlussjahr",
      message: "Wie können wir helfen?",
      submit: "Beratung anfragen",
      success: "Danke. Ein Northcrest-Berater wird sich melden.",
      error: "Etwas ist schiefgelaufen. Bitte versuche es erneut.",
      calendlyT: "Oder direkt buchen",
      calendlyD: "Calendly-Integration auf Anfrage verfügbar.",
      mapsT: "Besuche uns",
      phonesT: "Direkte Leitungen",
      offices: [
        { city: "Bern, Schweiz", role: "Hauptsitz" },
        { city: "San Diego, Kalifornien", role: "US-Geschäftsstelle" },
      ],
    },
    footer: {
      brandLine: "Exklusive Athletenberatung. Schweiz · Portugal · USA.",
      programs: "Programme",
      company: "Unternehmen",
      discover: "Entdecken",
      explore: "Entdecken",
      offices: "Standorte",
      legal: "Rechtliches",
      privacy: "Datenschutz",
      terms: "AGB",
      cookies: "Cookies",
      rights: "Alle Rechte vorbehalten.",
      newsletterTitle: "Neuigkeiten von Northcrest",
      newsletterKicker: "Neue Camps, Kurse und Partnerschaften — gelegentlich, nie Spam.",
      newsletterPlaceholder: "Deine E-Mail",
      newsletterCta: "Abonnieren",
      newsletterSuccess: "Danke. Wir melden uns bald.",
      newsletterError: "Etwas ist schiefgelaufen. Bitte versuche es erneut.",
      newsletterInvalid: "Bitte gib eine gültige E-Mail-Adresse ein.",
    },
  },

  pt: {
    nav: {
      home: "Início",
      about: "Sobre Nós",
      camps: "Camps",
      coachEd: "Formação de Treinadores",
      coachEdShort: "Formação",
      partners: "Parceiros",
      legal: "Legal",
      contact: "Contacto",
      universities: "Universidades",
      sports: "Modalidades",
      successStories: "Casos de Sucesso",
      resources: "Recursos",
      bookCta: "Marcar consulta",
    },
    common: {
      learnMore: "Saber mais",
      readMore: "Ler mais",
      explore: "Explorar",
      back: "Voltar",
      contactUs: "Contacta-nos",
      sendMessage: "Enviar mensagem",
      submitting: "A enviar…",
      ourOffices: "Os Nossos Escritórios",
      tagline: "Norte é a direção. Crest é o cume.",
      taglineSecondary: "Da Europa à Oportunidade.",
      seeAll: "Ver tudo",
    },
    home: {
      heroOverline: "Consultoria de Elite para Atletas · Delaware · Berna · Lisboa · San Diego",
      heroTitle1: "O Teu Potencial.",
      heroTitle2: "O Teu Futuro.",
      heroSubtitle:
        "Orientamos estudantes-atletas ambiciosos rumo a oportunidades académicas e desportivas nas universidades dos EUA.",
      ctaPrimary: "Marcar consulta",
      ctaSecondary: "Saber mais",
      whyTitle: "Porquê Northcrest",
      whyKicker: "Três pilares",
      pillars: [
        { k: "Excelência Académica", d: "Preparação académica rigorosa e colocação universitária." },
        { k: "Conquista Desportiva", d: "Desenvolvimento e contacto com treinadores americanos." },
        { k: "Oportunidade Internacional", d: "Uma ponte de confiança entre a Europa e os EUA." },
      ],
      processTitle: "O Nosso Processo",
      processKicker: "Um percurso medido. Seis passos deliberados.",
      processSteps: [
        { n: "01", t: "Avaliação", d: "Revisão completa do perfil académico e desportivo." },
        { n: "02", t: "Estratégia", d: "Plano de consultoria à medida." },
        { n: "03", t: "Universidades", d: "Programas curados em NCAA, NAIA e NJCAA." },
        { n: "04", t: "Treinadores", d: "Apresentações diretas a treinadores nos EUA." },
        { n: "05", t: "Colocação", d: "Candidaturas, bolsas e propostas." },
        { n: "06", t: "Sucesso", d: "Acompanhamento de longo prazo." },
      ],
      sportsTitle: "Modalidades que Apoiamos",
      sportsKicker: "Disciplinas onde a Northcrest aconselha hoje.",
      familyTitle: "Porque as Famílias Confiam em Nós",
      familyItems: [
        {
          t: "Orientação Profissional",
          d: "Navegar pelo caminho até às universidades americanas pode ser complexo e exigente. A Northcrest oferece orientação estruturada, transparente e profissional ao longo de cada fase do percurso — desde a avaliação do atleta e a seleção da universidade até à estratégia de recrutamento e ao apoio à candidatura. Trabalhamos de perto com atletas e famílias para garantir decisões informadas, construídas com base em clareza, confiança e sucesso a longo prazo.",
        },
        {
          t: "Experiência Internacional",
          d: "A Northcrest foi construída sobre a ligação entre a Suíça, Portugal e os Estados Unidos. A nossa perspetiva internacional permite-nos compreender tanto o ambiente europeu de desenvolvimento desportivo como as expectativas das universidades e treinadores americanos. Ao combinar compreensão cultural, conhecimento educativo e expertise desportiva, ajudamos as famílias a percorrer com confiança oportunidades além-fronteiras.",
        },
        {
          t: "Estratégia Personalizada",
          d: "Cada atleta tem uma história, um perfil e uma ambição únicas. Na Northcrest, não acreditamos em soluções únicas. Desenvolvemos estratégias individualizadas com base na capacidade desportiva, nos objetivos académicos, nas aspirações pessoais e no desenvolvimento de longo prazo. O nosso objetivo é identificar o caminho certo para cada estudante-atleta — não simplesmente o mais rápido.",
        },
        {
          t: "Desenvolvimento de Longo Prazo",
          d: "O nosso compromisso vai além da colocação universitária. A Northcrest concentra-se em construir bases sólidas para o sucesso académico, desportivo e pessoal a longo prazo. Procuramos apoiar os atletas no desenvolvimento de disciplina, liderança, confiança e experiência internacional que os beneficiarão muito para além dos seus anos no desporto universitário.",
        },
      ],
      testimonialsTitle: "Vozes",
      testimonialsKicker: "Famílias e atletas sobre a experiência Northcrest.",
      testimonials: [
        { q: "A Northcrest tratou o futuro da nossa filha como um banco privado trata um património.", a: "Pais · Davos" },
        { q: "A preparação e o contacto com os treinadores estiveram noutro nível.", a: "Atleta de Futebol · Porto" },
        { q: "Não são agentes. São consultores. Essa diferença é tudo.", a: "Pais · Lisboa" },
      ],
      ctaBlockTitle: "Começa a Tua Jornada",
      ctaBlockBody:
        "Uma conversa com a Northcrest é uma conversa sobre um futuro que poucos alcançarão. Convidamos-te a começar.",
    },
    about: {
      title: "Sobre Nós",
      kicker: "Northcrest Athletics",
      missionT: "Missão",
      missionParas: [
        "A Northcrest existe para orientar estudantes-atletas ambiciosos rumo a oportunidades académicas e desportivas transformadoras nos Estados Unidos.",
        "Através de consultoria de confiança, estratégia personalizada e apoio de longo prazo, ajudamos os atletas e as suas famílias a percorrer o caminho complexo entre o potencial e a oportunidade.",
        "A nossa missão não é simplesmente colocar atletas em universidades.",
        "A nossa missão é ajudar os jovens a construir um futuro definido pela educação, ambição, carácter e crescimento pessoal.",
      ],
      visionT: "Visão",
      visionParas: [
        "Tornarmo-nos a plataforma de consultoria internacional de atletas mais confiável a conectar a Europa e os Estados Unidos.",
        "A Northcrest imagina um futuro em que estudantes-atletas talentosos já não são limitados pela geografia, pelo acesso ou pela falta de orientação.",
        "Aspiramos a construir um ecossistema que capacite os atletas em cada etapa do seu percurso — desde o desenvolvimento e a colocação universitária até ao crescimento profissional, mentoria e oportunidades para a vida.",
        "A nossa visão de longo prazo vai além do desporto.",
        "Procuramos criar um impacto duradouro através da educação, da experiência internacional, da liderança e do desenvolvimento humano.",
      ],
      valuesT: "Valores Fundamentais",
      valuesItems: [
        {
          t: "Confiança",
          paras: [
            "As famílias colocam o seu futuro nas nossas mãos.",
            "Atuamos com honestidade, transparência, profissionalismo e integridade em cada decisão que tomamos.",
            "A confiança é o alicerce da Northcrest.",
          ],
        },
        {
          t: "Excelência",
          paras: [
            "Acreditamos que a excelência se constrói através da disciplina, da preparação, da consistência e da responsabilidade.",
            "A Northcrest persegue os mais elevados padrões na orientação académica e desportiva.",
          ],
        },
        {
          t: "Oportunidade",
          paras: [
            "Todo o atleta talentoso merece um caminho.",
            "Comprometemo-nos a abrir portas, criar acesso e ajudar jovens ambiciosos a alcançar todo o seu potencial.",
          ],
        },
        {
          t: "Pensamento de Longo Prazo",
          paras: [
            "Priorizamos o desenvolvimento sustentável em detrimento dos resultados de curto prazo.",
            "O nosso objetivo não são apenas colocações bem-sucedidas, mas futuros bem-sucedidos.",
          ],
        },
        {
          t: "Filosofia Centrada no Atleta",
          paras: [
            "Cada atleta é único.",
            "Oferecemos orientação individualizada com base em objetivos pessoais, aspirações académicas, capacidade desportiva e desenvolvimento de longo prazo.",
            "As pessoas vêm sempre antes das transações.",
          ],
        },
        {
          t: "Perspetiva Internacional",
          paras: [
            "A Northcrest foi construída sobre a ligação entre a Suíça, Portugal e os Estados Unidos.",
            "Abraçamos a diversidade cultural, o pensamento global e a oportunidade internacional.",
          ],
        },
      ],
      philosophyT: "Filosofia Northcrest",
      philosophyTagline1: "Norte é a direção.",
      philosophyTagline2: "Crest é o cume.",
      philosophyBody:
        "A Northcrest existe para orientar estudantes-atletas ambiciosos rumo ao mais alto nível de realização académica e desportiva nos Estados Unidos.",
      philosophyClose: "Da Europa à Oportunidade.",
      presenceT: "Presença Internacional",
      offices: [
        { city: "Dover", country: "Delaware, EUA", role: "Sede Global" },
        { city: "Berna", country: "Suíça", role: "Sede Europeia" },
        { city: "Lisboa", country: "Portugal", role: "Operações Europa" },
        { city: "San Diego", country: "Califórnia, EUA", role: "Operações EUA" },
      ],
      networkT: "Nota do Fundador",
      networkD:
        "Uma rede global de universidades, treinadores, mentores académicos e antigos atletas.",
      founderNoteT: "Nota do Fundador",
      founderNoteParas: [
        "Na Northcrest, acreditamos que o talento merece orientação, oportunidade e um caminho claro a seguir.",
        "O propósito da nossa empresa não é simplesmente ajudar atletas a chegarem às universidades, mas ajudar os jovens a desbloquear todo o seu potencial através da educação, do desporto e da experiência internacional.",
        "Fundámos a Northcrest para nos tornarmos uma ponte de confiança entre a ambição e a oportunidade — capacitando estudantes-atletas e as suas famílias a percorrerem uma das jornadas mais importantes das suas vidas com confiança, clareza e propósito.",
      ],
    },
    partners: {
      title: "Parceiros",
      kicker: "Uma rede curada. Construída sobre confiança.",
      intro: "A Northcrest trabalha em conjunto com clubes, escolas e instituições selecionadas na Europa e nos Estados Unidos — parceiros que partilham os nossos padrões de discrição, excelência e compromisso de longo prazo com os estudantes-atletas.",
      sections: [
        {
          slug: "clubs",
          t: "Clubes",
          d: "A Northcrest colabora com clubes europeus selecionados em diversas disciplinas para identificar, desenvolver e preparar estudantes-atletas ambiciosos para o percurso universitário americano. Cada parceria assenta em padrões partilhados e compromisso de longo prazo.",
        },
        {
          slug: "schools",
          t: "Escolas",
          d: "Em estreita colaboração com escolas internacionais e instituições académicas, garantimos que a excelência académica e a ambição desportiva avancem em paralelo — preparando estudantes-atletas para as universidades americanas mais competitivas.",
        },
        {
          slug: "institutional",
          t: "Parcerias Institucionais",
          d: "Uma rede crescente de universidades, federações e parceiros institucionais que partilham o nosso compromisso com o desenvolvimento atlético de longo prazo e uma orientação estruturada e de confiança através das fronteiras.",
        },
      ],
      namedPartners: {
        visitCta: "Visitar website",
        title: "Parceiros Anunciados",
        kicker: "Instituições com quem trabalhamos.",
        items: [
          { name: "Sporting Clube de Portugal", role: "Parceiro dos Camps", href: "https://www.sporting.pt", logo: "/partners/sporting-cp.png", d: "Uma das academias de formação mais respeitadas da Europa — parceiro âncora dos Northcrest × Sporting CP Soccer Camps." },
          { name: "Sportrail", role: "Parceiro de Formação", href: "https://www.sportrail.pt", logo: "/partners/sportrail.webp", d: "Líder português em formação certificada de treinadores de futebol (DGERT & IPDJ). Desde 2016, mais de 3.500 treinadores formados por um corpo docente de elite com mais de 70 formadores oriundos dos principais clubes europeus — a base institucional do programa de Coach Education da Northcrest." },
          { name: "United Portuguese S.E.S.", role: "Parceiro Comunitário · San Diego", logo: "/partners/upses.webp", href: "https://www.upses.com", d: "Desde 1922, o coração da comunidade portuguesa de San Diego em Point Loma — promovendo a fé, a cultura, a vida cívica e a união entre famílias. O nosso parceiro comunitário do Northcrest × Sporting CP Camp San Diego." },
          { name: "Arkansas Comets FC", role: "Clube Parceiro", logo: "/partners/comets.png", href: "https://www.arkansascometsfc.com", d: "Fundado em 1993 em Springdale, é o clube de futebol mais bem-sucedido do Noroeste do Arkansas — dos Comets Juniors à ECNL masculina e feminina (Sub-6 a Sub-19), dedicado a maximizar o potencial de cada jogador. O nosso clube parceiro do Northcrest × Sporting CP Camp Arkansas." },
        ],
      },
      closing: "As parcerias formam-se por convite. Para explorar uma colaboração com a Northcrest, escreve-nos.",
    },
    services: {
      title: "Serviços",
      kicker: "Uma consultoria completa ao longo do percurso.",
      items: [
        { t: "Avaliação do Atleta", d: "Análise académica, desportiva e pessoal." },
        { t: "Matching Universitário", d: "Programas alinhados com ambição e perfil." },
        { t: "Estratégia de Recrutamento", d: "Campanhas estruturadas, nunca improvisadas." },
        { t: "Contacto com Treinadores", d: "Relações diretas com staffs americanos." },
        { t: "Apoio à Candidatura", d: "Common App, ensaios, transcrições, exames." },
        { t: "Bolsas de Estudo", d: "Vias desportivas, académicas e baseadas em necessidade." },
        { t: "Consultoria à Família", d: "Um parceiro de confiança para os pais." },
        { t: "Desenvolvimento de Carreira", d: "Para além da colocação." },
        { t: "Preparação NIL", d: "Marca, imagem e Name-Image-Likeness." },
      ],
      featuredT: "Programas",
      featuredKicker: "Curados pela Northcrest.",
      featured: [
        { slug: "soccer-camps", t: "Soccer Camps", d: "Camps de futebol de alta performance nos EUA, em parceria com o Sporting Clube de Portugal.", cta: "Descobrir Camps" },
        { slug: "coach-education", t: "Formação de Treinadores", d: "Programas de desenvolvimento profissional para treinadores — em preparação com os nossos parceiros institucionais.", cta: "Saber Mais" },
      ],
      futureT: "Em Breve",
      future: [
        { t: "Northcrest Elite", d: "Círculo de mentoria por convite." },
        { t: "Northcrest Camps", d: "Showcases curados na Europa e nos EUA." },
        { t: "Northcrest Alumni", d: "Uma rede para a vida." },
      ],
    },
    camps: {
      title: "Soccer Camps 2027",
      kicker: "Em parceria com o Sporting Clube de Portugal.",
      intro: "Uma série limitada de camps de futebol de alta performance nos Estados Unidos, realizada em parceria com o Sporting Clube de Portugal — uma das academias de formação mais respeitadas da Europa. Orientada por treinadores formados no Sporting, para rapazes e raparigas ambiciosos dos 6 aos 17 anos.",
      confirmedLabel: "Datas confirmadas",
      tbaLabel: "Localizações adicionais em negociação",
      tbaBadge: "Em confirmação",
      tbaCard: "Localização a anunciar",
      tbaLocations: ["Oregon", "Massachusetts", "Raleigh, NC", "Texas"],
      ageLabel: "Idades",
      registerCta: "Inscrever",
      openingSoonCta: "Inscrições brevemente",
      registerSectionTitle: "Inscrição no Camp",
      registerSectionKicker: "Reserva o teu lugar. Vagas limitadas.",
      openingKicker: "Inscrições abrem em setembro de 2026",
      openingTitle: "Detalhes a divulgar em breve",
      openingBody: "Locais, datas e preços dos camps 2027 serão publicados em conjunto com a abertura das inscrições. Regista o teu interesse para seres notificado assim que as vagas estiverem disponíveis.",
      openingCta: "Registar interesse",
      partnershipLabel: "Em parceria com",
      feesLabel: "Investimento",
      feeTiers: { early: "Early Bird", regular: "Regular", late: "Late Bloomer" },
      feeTierDates: {
        early: "inscrição até 31 de Dezembro de 2026",
        regular: "inscrição até 31 de Março de 2027",
        late: "até ao 1º dia de camp",
      },
      confirmed: [
        { slug: "san-diego", city: "San Diego, CA", venue: "Point Loma Nazarene University", dateRange: "9 a 13 de Junho de 2027", ages: "6–17", fees: { early: 459, regular: 509, late: 559 }, registerHref: "https://www.sportingcampsandiego.us", partner: { name: "United Portuguese S.E.S.", logo: "/partners/upses.webp", href: "https://www.upses.com" } },
        { slug: "arkansas", city: "Springdale, AR", venue: "Brunner & Lay Soccer Complex", dateRange: "28 de Junho a 2 de Julho de 2027", ages: "6–17", fees: { early: 399, regular: 449, late: 499 }, registerHref: "https://www.sportingcamparkansas.us", partner: { name: "Arkansas Comets FC", logo: "/partners/comets.png", href: "https://www.arkansascometsfc.com" } },
        { slug: "artesia", city: "Artesia, CA", venue: "", dateRange: "14 a 18 de Junho de 2027", ages: "6–17" },
        { slug: "sarasota", city: "Sarasota, FL", venue: "", dateRange: "8 a 11 de Julho de 2027", ages: "6–17" },
      ],
      form: {
        athleteSectionTitle: "Atleta",
        firstName: "Primeiro Nome",
        lastName: "Apelido",
        dob: "Data de Nascimento",
        sex: "Sexo",
        sexOptions: [
          { v: "", l: "—" },
          { v: "male", l: "Masculino" },
          { v: "female", l: "Feminino" },
          { v: "other", l: "Prefiro não dizer" },
        ],
        position: "Posição",
        positionOptions: [
          { v: "", l: "—" },
          { v: "goalkeeper", l: "Guarda-Redes" },
          { v: "defender", l: "Defesa" },
          { v: "midfielder", l: "Médio" },
          { v: "forward", l: "Avançado" },
        ],
        club: "Clube Atual",
        guardianSectionTitle: "Encarregado de Educação",
        guardianName: "Nome Completo",
        guardianEmail: "E-mail",
        guardianPhone: "Telefone",
        guardianCountry: "País",
        campSectionTitle: "Escolha do Camp",
        campLabel: "Escolher Camp",
        campPlaceholder: "— Selecionar um camp —",
        addOnsSectionTitle: "Serviços Adicionais (opcional)",
        addOnsIntro: "Todos os preços em USD. Reservados na inscrição, prestados no local durante o camp.",
        addOns: {
          individual_training: {
            title: "Treino Individual",
            subtitle: "Sessões de 40 minutos com um treinador formado no Sporting.",
            tiers: [
              { v: "", l: "Nenhum", price: null },
              { v: "single", l: "Sessões avulsas", note: "$69 por sessão — até 2/dia", price: 69, perUnit: true },
              { v: "pack1", l: "Pack — 1 sessão por dia de camp", price: 249 },
              { v: "pack2", l: "Pack — 2 sessões por dia de camp", price: 459 },
            ],
            qtyLabel: "Número de sessões",
          },
          gk_training: {
            title: "Treino Individual de Guarda-Redes",
            subtitle: "Sessões de 40 minutos com um treinador especialista de guarda-redes.",
            tiers: [
              { v: "", l: "Nenhum", price: null },
              { v: "single", l: "Sessões avulsas", note: "$69 por sessão — até 2/dia", price: 69, perUnit: true },
              { v: "pack1", l: "Pack — 1 sessão por dia de camp", price: 249 },
              { v: "pack2", l: "Pack — 2 sessões por dia de camp", price: 459 },
            ],
            qtyLabel: "Número de sessões",
          },
          analyst: {
            title: "Feedback do Analista",
            subtitle: "Análise de desempenho individual pelo nosso analista. Até 1 sessão por dia de camp.",
            tiers: [
              { v: "", l: "Nenhum", price: null },
              { v: "verbal", l: "Sessão verbal de 20 minutos", price: 49, perUnit: true },
              { v: "full", l: "Análise completa + 30 min 1-on-1 + resumo escrito (relatório entregue no último dia de camp)", price: 89, perUnit: true },
              { v: "full_video", l: "Análise completa + 30 min 1-on-1 + resumo escrito + Vídeo de análise pessoal (relatório e vídeo entregues no último dia de camp)", price: 119, perUnit: true },
            ],
            qtyLabel: "Número de sessões",
          },
        },
        addOnsTotalLabel: "Total estimado (serviços adicionais)",
        campFeeLabel: "Inscrição no camp",
        grandTotalLabel: "Total Estimado",
        notesLabel: "Notas, informações médicas ou alergias",
        consentLabel: "Confirmo que sou o encarregado de educação legal do atleta e consinto no tratamento dos dados acima nos termos da",
        privacyLinkLabel: "Política de Privacidade",
        submit: "Enviar Inscrição",
        submitting: "A enviar…",
        success: "Inscrição recebida. Entraremos em contacto em breve.",
        error: "Algo correu mal. Por favor, revê o formulário e tenta novamente.",
        requiredMissing: "Por favor, preenche todos os campos obrigatórios.",
        consentRequired: "Por favor, confirma o consentimento parental antes de enviar.",
        registerAnother: "Inscrever outro atleta",
      },
    },
    coachEducation: {
      title: "Formação de Treinadores",
      kicker: "Programas em preparação. Primeiro curso com inscrições abertas.",
      intro: "A Northcrest está a desenvolver uma série curada de programas de formação profissional para treinadores — concebidos em conjunto com os nossos parceiros institucionais na Europa e nos Estados Unidos. Precisos. Deliberados. Sem compromissos.",
      featuredLabel: "Inscrições Abertas",
      browseLabel: "Ver Programas",
      featured: [
        {
          partner: "Em colaboração com a Sportrail",
          title: "The Spanish Way",
          subtitle: "Metodologia de Desenvolvimento de Jovens",
          d: "Seis sessões online ao vivo sobre a metodologia por trás do desenvolvimento de jovens em Espanha — modelo de jogo, jogo posicional, desenho do microciclo e o percurso desde a academia até ao futebol sénior. Ministradas por profissionais que trabalham atualmente no futebol de academia de elite.",
          facts: [
            { k: "Formato", v: "6 × 2h ao vivo · online · ~20h CPD" },
            { k: "Primeira coorte", v: "20 Out → 5 Nov 2026 · 3 semanas" },
            { k: "Investimento", v: "$227 early bird · pagamento único ou 3 mensalidades" },
            { k: "Limite por coorte", v: "40 treinadores" },
            { k: "Certificação", v: "Sportrail · certificação DGERT & IPDJ" },
          ],
          registerCta: "Inscrever no site Sportrail",
          registerHref: "https://sportrailfootball.com/courses/spanish-way?ref=northcrest",
        },
        {
          partner: "Em colaboração com a Sportrail",
          title: "Goalkeeper Coaching",
          subtitle: "The European Approach",
          d: "Seis sessões online ao vivo para treinadores de guarda-redes — desde a criação de um departamento de GR e o planeamento do microciclo, à defesa da baliza, jogo de pés no build-up, percursos de formação e frameworks de scouting. Ministradas por profissionais que trabalham no Newcastle United, Norwich City, Real Salt Lake, Coleraine FC e ex-Marseille/Barcelona.",
          facts: [
            { k: "Formato", v: "6 × 2h ao vivo · online · 20h CPD" },
            { k: "Próxima coorte", v: "22 Set → 8 Out 2026 · 3 semanas" },
            { k: "Investimento", v: "$277 · pagamento único ou 3 mensalidades" },
            { k: "Limite por coorte", v: "40 treinadores" },
            { k: "Certificação", v: "Sportrail · certificação DGERT & IPDJ" },
          ],
          registerCta: "Inscrever no site Sportrail",
          registerHref: "https://sportrailfootball.com/courses/goalkeeper-coaching?ref=northcrest",
        },
        {
          partner: "Em colaboração com a Sportrail",
          title: "Scouting in Football",
          subtitle: "From Talent to Contract",
          d: "Seis sessões online ao vivo sobre o ofício do scouting no futebol — conceitos e mitos, o papel do scout no clube, ambientes de elite da Premier League, geoestratégia, dados e tecnologia, e relatórios que informam decisões. Ministrado por João Ferreira, International First-Team Scout no Tottenham Hotspur.",
          facts: [
            { k: "Formato", v: "6 × 2h ao vivo · online · 20h CPD" },
            { k: "Próxima coorte", v: "15 Set → 16 Out 2026" },
            { k: "Investimento", v: "$227 early bird · pagamento único ou 3 mensalidades" },
            { k: "Limite por coorte", v: "40 treinadores" },
            { k: "Certificação", v: "Sportrail · certificação DGERT & IPDJ" },
          ],
          registerCta: "Inscrever no site Sportrail",
          registerHref: "https://sportrailfootball.com/courses/scouting?ref=northcrest",
        },
      ],
      upcomingLabel: "Também em desenvolvimento",
      upcoming: [
        { t: "Formação de Treinadores de Guarda-Redes", d: "Um currículo estruturado nos princípios modernos do desenvolvimento do guarda-redes." },
        { t: "Metodologia do Treino", d: "Design, estrutura e periodização por trás de programas de alta performance." },
        { t: "Outros Programas", d: "Formações adicionais em disciplinas posicionais, físicas e táticas." },
      ],
      closing: "Para seres notificado quando os próximos programas abrirem, escreve-nos.",
      contactCta: "Registar interesse",
    },
    sports: {
      title: "Modalidades que Apoiamos",
      kicker: "Disciplinas onde a Northcrest aconselha hoje.",
      list: [
        { slug: "soccer", t: "Futebol" },
        { slug: "tennis", t: "Ténis" },
        { slug: "golf", t: "Golfe" },
      ],
      futureT: "Outras Modalidades",
      futureD: "Qualquer outra modalidade será avaliada caso a caso.",
      detail: {
        overview: "Resumo",
        opportunities: "Oportunidades",
        pathways: "Caminhos Universitários",
        timeline: "Cronograma de Recrutamento",
        faqs: "Perguntas Frequentes",
        timelineSteps: [
          { y: "Ano 1", t: "Fundação", d: "Mapeamento académico, perfil, primeiros vídeos." },
          { y: "Ano 2", t: "Posicionamento", d: "Showcases, estratégia de exames, programas." },
          { y: "Ano 3", t: "Contacto", d: "Comunicação com treinadores, visitas, shortlist." },
          { y: "Ano 4", t: "Colocação", d: "Propostas, assinatura, transição." },
        ],
      },
    },
    universities: {
      title: "Universidades",
      kicker: "Uma visão interactiva do panorama universitário americano.",
      filterAll: "Todas as Divisões",
      categories: ["NCAA Divisão I", "NCAA Divisão II", "NCAA Divisão III", "NAIA", "NJCAA"],
      searchPlaceholder: "Procurar por estado, universidade ou divisão…",
      hintMap: "Seleciona um estado para ver programas.",
      noResults: "Sem resultados para esta selecção.",
    },
    success: {
      title: "Histórias de Sucesso",
      kicker: "Um relato medido dos atletas colocados.",
      placeholder:
        "Estudos de caso serão adicionados aqui à medida que famílias e atletas consintam em partilhar. A discrição é, e será sempre, o nosso primeiro princípio.",
    },
    resources: {
      title: "Recursos",
      kicker: "Guias e leituras para famílias.",
      categories: ["Blog", "Guias", "Bolsas", "Visão Geral NCAA", "Cronograma", "Pais", "Checklist"],
      items: [
        { c: "Visão Geral NCAA", t: "Compreender as Divisões I, II, III", d: "Como se estrutura o desporto universitário nos EUA." },
        { c: "Bolsas", t: "A Verdade sobre as Bolsas Desportivas", d: "Um guia medido, para além dos mitos." },
        { c: "Cronograma", t: "Calendário de Recrutamento de Quatro Anos", d: "O que fazer, e quando." },
        { c: "Pais", t: "Uma Carta aos Pais", d: "O que pedimos às famílias." },
        { c: "Guias", t: "Checklist do Estudante-Atleta", d: "Padrão institucional para organização." },
        { c: "Blog", t: "Da Europa à Oportunidade", d: "Notas de campo da Northcrest." },
      ],
    },
    contact: {
      title: "Inicia uma Conversa",
      kicker: "Consulta por convite. Respondemos pessoalmente.",
      name: "Nome Completo",
      email: "Endereço de Email",
      phone: "Telefone",
      country: "País",
      sport: "Modalidade",
      year: "Ano de Conclusão",
      message: "Como podemos ajudar?",
      submit: "Pedir consulta",
      success: "Obrigado. Um consultor Northcrest entrará em contacto.",
      error: "Algo correu mal. Tenta novamente.",
      calendlyT: "Ou marque directamente",
      calendlyD: "Integração Calendly disponível a pedido.",
      mapsT: "Visita-nos",
      phonesT: "Linhas Diretas",
      offices: [
        { city: "Berna, Suíça", role: "Sede" },
        { city: "San Diego, Califórnia", role: "Operações EUA" },
      ],
    },
    footer: {
      brandLine: "Consultoria de elite para atletas. Suíça · Portugal · EUA.",
      programs: "Programas",
      company: "Empresa",
      discover: "Descobrir",
      explore: "Explorar",
      offices: "Escritórios",
      legal: "Legal",
      privacy: "Privacidade",
      terms: "Termos",
      cookies: "Cookies",
      rights: "Todos os direitos reservados.",
      newsletterTitle: "Novidades da Northcrest",
      newsletterKicker: "Novos camps, cursos e parcerias — ocasionalmente, nunca spam.",
      newsletterPlaceholder: "O teu email",
      newsletterCta: "Subscrever",
      newsletterSuccess: "Obrigado. Entraremos em contacto.",
      newsletterError: "Algo correu mal. Por favor, tenta novamente.",
      newsletterInvalid: "Por favor, indica um email válido.",
    },
  },
};
